import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../AuthProvider'
import { useFixturesFromQuery } from '../fixtures'
import { WorkspaceAccountProvider } from '../WorkspaceAccountProvider'
import { ClientMobileDrawer, ClientSidebar } from './ClientNav'
import { WorkspaceChrome } from './WorkspaceChrome'
import { ThemeToggle } from '../../components/ui/ThemeToggle'
import { InboxBell } from '../../shared/InboxBell'
import { api } from '../api'
import { useAppPath } from '../nav'

function topbarTitle(pathname: string): string {
  if (pathname.startsWith('/app/accounts')) return 'Trading Accounts'
  if (pathname.startsWith('/app/trade')) return 'Trade Now'
  if (pathname.startsWith('/app/deposits')) return 'Deposits'
  if (pathname.startsWith('/app/withdrawals')) return 'Withdrawals'
  if (pathname.startsWith('/app/trading-history')) return 'Trading History'
  if (pathname.startsWith('/app/transactions')) return 'Transactions'
  if (pathname.startsWith('/app/profile')) return 'Profile'
  if (pathname.startsWith('/app/support')) return 'Support'
  if (pathname.startsWith('/app/more')) return 'More'
  return 'Trading'
}

function ProfileMenu({ label }: { label: string }) {
  const { logout } = useAuth()
  const appPath = useAppPath()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div className="cp-topbar__profile-wrap" ref={root}>
      <button
        type="button"
        className="cp-topbar__profile"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </button>
      {open ? (
        <div className="cp-topbar__profile-menu" role="menu">
          <Link role="menuitem" to={appPath('/app/profile')} onClick={() => setOpen(false)}>
            Profile
          </Link>
          <Link role="menuitem" to={appPath('/app/support')} onClick={() => setOpen(false)}>
            Support
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              logout()
            }}
          >
            Logout
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function ClientLayout() {
  const { isAuthenticated, client } = useAuth()
  const fixtures = useFixturesFromQuery()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  if (!isAuthenticated && !fixtures) {
    return <Navigate to="/app/login" replace state={{ from: location.pathname }} />
  }

  const profileLabel = fixtures
    ? 'Alex'
    : (client?.firstName || '').trim() || 'Profile'

  return (
    <div className={`cp-shell cp-shell--broker${menuOpen ? ' is-menu-open' : ''}`}>
      <a className="skip-link" href="#client-main">
        Skip to content
      </a>
      <ClientSidebar />
      <ClientMobileDrawer open={menuOpen} onClose={closeMenu} />
      <div className="cp-main-wrap">
        <header className="cp-topbar cp-topbar--broker">
          <div className="cp-topbar__lead">
            <button
              type="button"
              className="cp-topbar__menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="cp-mobile-drawer"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="cp-topbar__menu-bars" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
            </button>
            <div>
              <p className="cp-topbar__eyebrow">Perfect Predict</p>
              <strong>{topbarTitle(location.pathname)}</strong>
            </div>
          </div>
          <div className="cp-topbar__actions">
            {fixtures ? <span className="cp-chip cp-chip--warn">Preview</span> : null}
            {!fixtures ? (
              <InboxBell
                load={() =>
                  api.notifications().then((r) => ({
                    unreadCount: r.unreadCount,
                    notifications: r.notifications,
                  }))
                }
                markRead={(id) =>
                  api.notificationsRead(id).then((r) => ({ unreadCount: r.unreadCount }))
                }
              />
            ) : null}
            <ThemeToggle />
            <ProfileMenu label={profileLabel} />
          </div>
        </header>
        <main id="client-main" className="cp-main">
          <WorkspaceAccountProvider>
            <WorkspaceChrome />
          </WorkspaceAccountProvider>
        </main>
      </div>
    </div>
  )
}
